import { Injectable } from '@angular/core';
import { ActivationEnd } from '@angular/router';

import { BetslipConfig, ReturnsMessageConfig } from '@frontend/sports/common/client-config-data-access';
import { RouteTag } from '@frontend/sports/common/core/data-access/route';
import { emitAfter } from '@frontend/sports/common/core/utils/emit-after';
import { Nullable } from '@frontend/sports/common/core/utils/extended-types';
import { RouterEventsService } from '@frontend/sports/common/core/utils/router-events';
import { OddsDisplayFormat } from '@frontend/sports/types/models/user-settings';
import { UserStateService } from '@frontend/sports/user/feature';
import { Store } from '@ngrx/store';
import { isAccaBoostTokenSelector } from 'packages/sports/web/app/src/acca-boost-base/acca-boost-utils';
import { EMPTY, Observable, distinctUntilChanged, iif, map, merge, of, shareReplay, switchMap, take, withLatestFrom } from 'rxjs';

import { IBetslipRootState } from '../../base/store/state';
import MultiReturnsMessageService from '../../modules/multi-returns-message/multi-returns-message.service';

@Injectable()
export class MultiReturnsMessageNotificationService {
    private readonly route$: Observable<ActivationEnd | undefined> = this.routeEvents.currentActivationEnd.pipe(shareReplay(1));

    private readonly timeout$ = this.route$.pipe(
        map((route) => this.isTimeoutEnabled(route)),
        emitAfter(null, () => this.config.persistenceTime * 1000),
        take(1),
    );

    private readonly selectedToken$ = this.store.select(isAccaBoostTokenSelector);

    constructor(
        private multiReturnMessageService: MultiReturnsMessageService,
        private store: Store<IBetslipRootState>,
        private config: ReturnsMessageConfig,
        private betslipConfig: BetslipConfig,
        private routeEvents: RouterEventsService,
        private userStateService: UserStateService,
    ) {}

    getMessage$(): Observable<Nullable<string>> {
        return this.betslipConfig.isMultiReturnsMessageEnabled ? this.getData$() : EMPTY;
    }

    private getData$(): Observable<Nullable<string>> {
        const oddsDisplayFormat$ = this.userStateService.userState$().pipe(
            map((state) => state.oddsFormat ?? OddsDisplayFormat.EU),
            distinctUntilChanged(),
        );

        const message$ = this.selectedToken$.pipe(
            withLatestFrom(oddsDisplayFormat$),
            switchMap(([isSelected, oddsFormat]) => {
                return this.multiReturnMessageService.getMessage$(oddsFormat, isSelected);
            }),
        );

        const hideAfterTimeout$: Observable<null> = message$.pipe(
            withLatestFrom(this.route$),
            switchMap(([message]) => iif(() => !!message, this.timeout$, of(null))),
        );

        return merge(message$, hideAfterTimeout$);
    }

    private isTimeoutEnabled(event: ActivationEnd | undefined): boolean {
        return event?.snapshot.data['tag'] !== RouteTag.MultiBuilder;
    }
}
